import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideUsers,
  lucideCalendarDays,
  lucideReceipt,
  lucideFolderOpen,
  lucideFileText,
  lucideSettings,
  lucideLogOut,
  lucideBuilding2,
  lucideChevronsUpDown,
  lucideMegaphone,
  lucideMessageCircleQuestion,
  lucideGraduationCap,
  lucideEllipsisVertical,
} from '@ng-icons/lucide';
import { take } from 'rxjs';
import {
  HlmSidebarImports,
  HlmAvatarImports,
  HlmButton,
  HlmButtonImports,
  HlmSelectImports,
  HlmAlertDialogImports,
  BrnAlertDialogImports,
  HlmDropdownMenuImports,
} from '@pagai-fe/ui';
import { AuthService } from '../../../auth/services/auth.service';
import { LoggedUserService } from '../../services/logged-user.service';
import { AuthorizationService } from '../../services/authorization.service';
import { TenantConfigService } from '../../services/tenant-config.service';
import { IntercomService } from '../../services/intercom.service';
import { TutorialService } from '../../services/tutorial.service';
import { SIDEBAR_ITEMS, type SidebarItem } from '../../config/sidebar.config';
import { TenantSelectorDialogComponent } from '../tenant-selector-dialog/tenant-selector-dialog.component';
import { WelcomeDialogComponent } from '../welcome-dialog/welcome-dialog.component';
import { TutorialPickerDialogComponent } from '../tutorial-picker-dialog/tutorial-picker-dialog.component';

/**
 * Main sidebar component for Pagai HR navigation
 * Displays logo, navigation menu items, and user info with logout
 */
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    NgIcon,
    HlmSidebarImports,
    HlmAvatarImports,
    HlmButton,
    HlmButtonImports,
    HlmSelectImports,
    HlmAlertDialogImports,
    BrnAlertDialogImports,
    HlmDropdownMenuImports,
    TenantSelectorDialogComponent,
    WelcomeDialogComponent,
    TutorialPickerDialogComponent,
  ],
  providers: [
    provideIcons({
      lucideUsers,
      lucideCalendarDays,
      lucideReceipt,
      lucideFolderOpen,
      lucideFileText,
      lucideSettings,
      lucideLogOut,
      lucideBuilding2,
      lucideChevronsUpDown,
      lucideMegaphone,
      lucideMessageCircleQuestion,
      lucideGraduationCap,
      lucideEllipsisVertical,
    }),
  ],
  templateUrl: './app-sidebar.component.html',
})
export class AppSidebarComponent {
  private readonly _router = inject(Router);
  private readonly _authService = inject(AuthService);
  private readonly _loggedUserService = inject(LoggedUserService);
  private readonly _authorizationService = inject(AuthorizationService);
  private readonly _tenantConfigService = inject(TenantConfigService);
  private readonly _intercomService = inject(IntercomService);
  private readonly _tutorialService = inject(TutorialService);

  /** Reference to tenant selector dialog */
  protected readonly tenantDialogRef =
    viewChild.required<TenantSelectorDialogComponent>('tenantDialog');

  /** Reference to tutorial picker dialog */
  protected readonly tutorialPickerRef =
    viewChild.required<TutorialPickerDialogComponent>('tutorialPicker');

  /** All sidebar navigation items from config */
  private readonly _allItems: SidebarItem[] = SIDEBAR_ITEMS;

  /** Whether current user is a Labor Consultant */
  protected readonly isLaborConsultant = this._authorizationService.isLaborConsultant;

  /** Filtered sidebar items based on user roles */
  protected readonly items = computed(() => {
    const userRoles = this._authorizationService.userRoles();

    const filteredItems = this._allItems.filter((item) => {
      // Hidden if the tenant has the corresponding feature disabled
      if (item.feature && !this._tenantConfigService.hasFeature(item.feature)) {
        return false;
      }
      // If no roles specified, item is visible to all
      if (!item.roles || item.roles.length === 0) {
        return true;
      }
      // Check if user has any of the required roles
      return item.roles.some((role) => userRoles.includes(role));
    });

    return filteredItems;
  });

  /** Current user data as signal (converted from observable) */
  protected readonly user = toSignal(this._loggedUserService.user$);

  /** All tenants the user has access to (uses tenantsSubject$ for better reactivity) */
  protected readonly tenants = toSignal(this._loggedUserService.tenantsSubject$, { initialValue: [] });

  /** Currently selected tenant ID (converted from observable) */
  protected readonly selectedTenantId = toSignal(this._loggedUserService.selectedTenantIdSubject$, {
    initialValue: null,
  });

  /** Whether user has multiple tenants (converted from observable) */
  protected readonly hasMultipleTenants = toSignal(this._loggedUserService.hasMultipleTenants$, {
    initialValue: false,
  });

  /** Selected tenant (converted from observable) */
  protected readonly selectedTenant = toSignal(this._loggedUserService.selectedTenant$, {
    initialValue: null,
  });

  /** Tenant initials for avatar fallback (from businessName) */
  protected readonly tenantInitials = computed(() => {
    const tenant = this.selectedTenant();
    if (!tenant?.businessName) {
      return '?';
    }
    // Split by non-alphanumeric characters (spaces, dashes, @, etc.)
    const words = tenant.businessName.split(/[^a-zA-Z0-9]+/).filter((w) => w.length > 0);
    if (words.length >= 2) {
      return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
    }
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return '?';
  });

  /** User initials for avatar fallback */
  protected readonly userInitials = computed(() => {
    const currentUser = this.user();
    if (!currentUser?.name || !currentUser?.surname) {
      return '?';
    }
    return `${currentUser.name.charAt(0)}${currentUser.surname.charAt(0)}`.toUpperCase();
  });

  /** User full name */
  protected readonly userName = computed(() => {
    const currentUser = this.user();
    if (!currentUser?.name) {
      return 'Utente';
    }
    return currentUser.surname ? `${currentUser.name} ${currentUser.surname}` : currentUser.name;
  });

  /**
   * Check if a path is currently active
   * @param path - The path to check
   * @returns True if the current URL starts with the path
   */
  protected isActive(path: string): boolean {
    return this._router.url.startsWith(path);
  }

  /**
   * Handle tenant selection change
   * @param tenantId - The selected tenant ID (from brn-select valueChange)
   */
  protected onTenantChange(tenantId: string | (string | null)[] | null | undefined): void {
    if (typeof tenantId === 'string') {
      this._loggedUserService.selectTenant(tenantId);
    }
  }

  /**
   * Open the tenant selector dialog (for Labor Consultants)
   */
  protected openTenantDialog(): void {
    this.tenantDialogRef().open();
  }

  /**
   * Opens the Intercom help messenger
   */
  protected openHelp(): void {
    this._intercomService.show();
  }

  /**
   * Navigate to the Settings page from the user menu.
   */
  protected goToSettings(): void {
    void this._router.navigate(['/settings']);
  }

  /** Logout confirmation dialog open state. */
  private readonly _logoutDialogOpen = signal(false);
  protected readonly logoutDialogOpen = this._logoutDialogOpen.asReadonly();

  /**
   * Open the logout confirmation dialog. The actual logout fires only
   * after the user confirms.
   * Opens the tutorial picker dialog
   */
  protected openTutorialPicker(): void {
    this.tutorialPickerRef().open();
  }

  /**
   * Re-opens the welcome dialog (same one shown on first access).
   * Triggered by clicking the sidebar logo.
   */
  protected openWelcome(): void {
    this._tutorialService.requestWelcome();
  }

  /**
   * Logout the user and navigate to login page
   */
  protected logout(): void {
    this._logoutDialogOpen.set(true);
  }

  protected onLogoutDialogStateChange(state: 'open' | 'closed'): void {
    if (state === 'closed') this._logoutDialogOpen.set(false);
  }

  protected cancelLogout(): void {
    this._logoutDialogOpen.set(false);
  }

  /** Confirmed logout: clear session and return to login. */
  protected confirmLogout(): void {
    this._logoutDialogOpen.set(false);
    this._authService
      .logout()
      .pipe(take(1))
      .subscribe(() => {
        void this._router.navigate(['/']);
      });
  }
}
